<template>
  <div class="sf-card">
    <h2>Choose an objective and then a game to earn Maths Points</h2>
    <div
      v-for="cat in rootCategories"
      :key="cat.id + '_' + currentYear"
      class="tw-mb-4 last:tw-mb-0"
    >
      <ul>
        <DbMathLevelItem
          :category="cat"
          :all-categories="allCategories"
          :all-levels="allLevels"
          :students="students"
          :teacher-mode="isTeacher"
          :current-user="$currentUser.value"
          :default-year="defaultYear"
          :show-year-paging="showYearPaging"
        />
      </ul>
    </div>
    <div v-if="isIndividual" class="tw-grid tw-grid-cols-2 tw-mb-2">
      <div>
        <a
          href=""
          v-if="defaultYear > 1"
          @click.prevent="offsetDefaultYear(-1)"
          class="primary"
        >
          &lt; Previous Year
        </a>
      </div>
      <div class="tw-text-right">
        <a
          href=""
          v-if="defaultYear < 6"
          @click.prevent="offsetDefaultYear(1)"
          class="primary"
        >
          Next Year &gt;
        </a>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import apiCall from "@/func/net/apiCall";
import userHasRole from "@/func/user/userHasRole";
import { Dict, SfUser } from "@/types/generalTypes";
import {
  SfMathsLevelExpandable,
  SfMathsLevelCategory,
} from "@/types/mathsTypes";
import { defineComponent } from "vue";
import DbMathLevelItem from "./math-levels/DbMathLevelItem.vue";
import { setIndividualMathsLevel } from "@/state/appState";

export default defineComponent({
  components: { DbMathLevelItem },
  props: {
    defaultYear: { type: Number, default: 1 },
    showYearPaging: { type: Boolean, default: false },
    teacherMode: { type: Boolean, default: false },
  },
  data() {
    return {
      allCategories: [] as SfMathsLevelCategory[],
      allLevels: [] as SfMathsLevelExpandable[],
      expanded: [] as number[],
      currentYear: 1,
      students: {} as Dict<SfUser>,
    };
  },
  computed: {
    isIndividual(): boolean {
      return userHasRole(this.$currentUser.value, "individual");
    },
    isTeacher(): boolean {
      return this.teacherMode && userHasRole(this.$currentUser.value, "teacher");
    },
    rootCategories(): SfMathsLevelCategory[] {
      return this.allCategories.filter((cat) => !cat.parent_category_id);
    },
  },
  mounted() {
    this.currentYear = this.defaultYear;
    this.refreshLevels();
    this.refreshStudents();
  },
  methods: {
    async refreshLevels() {
      const catResData = await apiCall("math-level-category", {
        all: 1,
        sort: "sort",
      });
      if (!catResData.success) {
        return;
      }
      this.allCategories = catResData.data.items;
      const levelResData = await apiCall("math-objective/home");
      if (levelResData.success) {
        this.allLevels = levelResData.data.items;
      }
    },
    async refreshStudents() {
      if (!this.isTeacher) {
        return;
      }
      const resData = await apiCall("student/get-active-students", {
        expand: "fullName",
      });
      if (resData.success) {
        const newStudents: Dict<SfUser> = {};
        for (let it of resData.data?.items) {
          newStudents[it.id] = it;
        }
        this.students = newStudents;
      }
    },
    async offsetDefaultYear(offset: number) {
      const level = this.defaultYear + offset;
      if (level < 1 || level > 6) {
        return;
      }
      const resData = await apiCall("student/set-individual-maths-level", {
        level,
      });
      if (resData.success) {
        setIndividualMathsLevel(parseInt(resData.data.level));
      }
    },
    toggleCategory(categoryId: number) {
      const pos = this.expanded.indexOf(categoryId);
      if (pos < 0) {
        this.expanded.push(categoryId);
      } else {
        this.expanded.splice(pos, 1);
      }
    },
  },
});
</script>
